import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppContextRepository } from '@shared/services/repositories/app-context-repository';
import { ToasterService } from '@shared/services/toaster/toaster';
import { AppMasterContext, CreateAppContextRequest, UpdateAppMasterContextRequest } from '@shared/types/master-context.model';
import { OrganizationContextService } from '@shared/services/context/organization-context.service';
import { SchoolContextService } from '@shared/services/context/school-context.service';
import { AppMasterContextCategory } from '@shared/enums/master-context.enum';

@Component({
    selector: 'app-context-dialog',
    templateUrl: './app-context-dialog.component.html',
    styleUrls: ['./app-context-dialog.component.css'],
    standalone: false,
})
export class AppContextDialogComponent implements OnInit {
    form!: FormGroup;
    isLoading = false;
    isEdit = false;
    isView = false;
    isDialog = false;
    isContextFixed = false;
    contextType: AppMasterContextCategory = AppMasterContextCategory.DEPARTMENT;
    contextOptions = Object.entries(AppMasterContextCategory).map(([key, value]) => ({ label: key, value }));
    @Input() isSchoolContext = true; // default to school context, can be overridden by parent component
    
    title = '';
    nameLabel = 'Name';
    private currentItemId?: string;
    private originalItem?: AppMasterContext;

    constructor(
        private formBuilder: FormBuilder,
        private appContextRepo: AppContextRepository,
        private toaster: ToasterService,
        private orgContext: OrganizationContextService,
        private schoolContext: SchoolContextService,
        private route: ActivatedRoute,
        private router: Router,
        @Optional() public dialogRef: MatDialogRef<AppContextDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: { isEdit: boolean, isView?: boolean, contextType?: AppMasterContextCategory, item?: AppMasterContext, isSchoolContext?: boolean }
    ) {
        this.isDialog = !!this.dialogRef;
    }

    ngOnInit(): void {
        this.initializeComponent();
    }

    private initializeComponent(): void {
        if (this.isDialog && this.data) {
            this.isEdit = this.data.isEdit;
            this.isView = !!this.data.isView;
            this.isSchoolContext = this.data.isSchoolContext ?? this.isSchoolContext;
            this.isContextFixed = !!this.data.contextType;
            this.contextType = this.data.contextType || AppMasterContextCategory.DEPARTMENT;
            this.updateLabels();
            this.buildForm();
            if ((this.isEdit || this.isView) && this.data.item) {
                this.currentItemId = this.data.item.id;
                this.originalItem = this.data.item;
                this.populateForm(this.data.item);
            }
        } else {
            // Handle as standalone page
            const snapshotTypeParam = this.route.snapshot.params['type']?.toUpperCase();
            const hasSnapshotTypeParam = !!snapshotTypeParam &&
                !!AppMasterContextCategory[snapshotTypeParam as keyof typeof AppMasterContextCategory];
            let hasExplicitType = !!this.route.snapshot.data['type'] || hasSnapshotTypeParam;

            this.route.data.subscribe(data => {
                if (data['type']) {
                    this.contextType = data['type'] as AppMasterContextCategory;
                    hasExplicitType = true;
                }
                if (data['isView']) {
                    this.isView = data['isView'];
                }
            });

            this.route.params.subscribe(params => {
                const id = params['id'];
                const typeParam = params['type']?.toUpperCase();
                
                if (typeParam && AppMasterContextCategory[typeParam as keyof typeof AppMasterContextCategory]) {
                    this.contextType = AppMasterContextCategory[typeParam as keyof typeof AppMasterContextCategory];
                    hasExplicitType = true;
                }

                // If path contains 'view', it's view mode
                this.isView = this.router.url.includes('/view/');
                this.isEdit = !!id && !this.isView;
                this.isContextFixed = hasExplicitType;
                this.currentItemId = id;
                this.updateLabels();
                this.buildForm();

                if ((this.isEdit || this.isView) && id) {
                    this.loadItemData(id);
                }
            });
        }
    }

    private updateLabels(): void {
        const typeStr = this.contextType === AppMasterContextCategory.DEPARTMENT ? 'Department' :
                       this.contextType === AppMasterContextCategory.DESIGNATION ? 'Designation' :
                       this.contextType === AppMasterContextCategory.EXAM_TYPE ? 'Exam Type' : 'Context';
        
        if (this.isView) {
            this.title = `View ${typeStr} Details`;
        } else {
            this.title = `${this.isEdit ? 'Edit' : 'Add New'} ${typeStr}`;
        }
        this.nameLabel = `${typeStr} Name`;
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({
            context: [this.isContextFixed ? this.contextType : null, [Validators.required]],
            name: ['', [Validators.required, Validators.maxLength(100)]],
            description: ['', [Validators.maxLength(500)]],
            isActive: [true]
        });

        if (!this.isEdit && !this.isView && this.isContextFixed) {
            this.form.get('context')?.disable({ emitEvent: false });
        }

        if (this.isEdit) {
            this.form.get('context')?.disable({ emitEvent: false });
        }

        if (this.isView) {
            this.form.disable();
        }
    }

    private populateForm(item: AppMasterContext): void {
        this.form.patchValue({
            context: item.context || this.contextType,
            name: item.name,
            description: item.description,
            isActive: item.isActive
        });

        if (this.isView) {
            this.form.disable();
        }
    }

    private async loadItemData(id: string) {
        this.isLoading = true;
        try {
            const res = await this.appContextRepo.getById(id);
            if (res.success && res.data) {
                this.originalItem = res.data;
                this.populateForm(res.data);
            } else {
                this.toaster.showError(res.title || 'Failed to load data');
                this.close();
            }
        } catch (error) {
            this.toaster.showError('An error occurred while loading content');
            this.close();
        } finally {
            this.isLoading = false;
        }
    }

    async save() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.toaster.showError('Please fill in all required fields');
            return;
        }

        this.isLoading = true;
        const formValue = this.form.getRawValue();

        try {
            if (this.isEdit && this.currentItemId) {
                // Check if status changed
                if (this.originalItem && formValue.isActive !== this.originalItem.isActive) {
                    await this.appContextRepo.toggleStatus(this.currentItemId);
                }

                const updateRequest: UpdateAppMasterContextRequest = {
                    id: this.currentItemId,
                    name: formValue.name,
                    description: formValue.description
                };
                const result = await this.appContextRepo.update(updateRequest);
                if (result.success) {
                    this.toaster.showSuccess(`${this.contextType} updated successfully`);
                    this.close(true);
                } else {
                    this.toaster.showError(result.title || `Failed to update ${this.contextType}`);
                }
            } else {
                const org = this.orgContext.getCurrentOrg();
                const school = this.schoolContext.getCurrentSchool();

                const createRequest: CreateAppContextRequest = {
                    ...formValue,
                    tenantCode: org!.tenantId || '',
                    schoolCode: this.isSchoolContext ? school?.code : undefined
                };

                const endpoint = this.isSchoolContext ? 'api/AppMasterContext/CreateSchoolContext' : 'api/AppMasterContext/CreateOrgContext';
                const result = await this.appContextRepo.create(endpoint, createRequest);
                if (result.success) {
                    this.toaster.showSuccess(`${this.contextType} created successfully`);
                    this.close(true);
                } else {
                    this.toaster.showError(result.title || `Failed to create ${this.contextType}`);
                }
            }
        } catch (error) {
            this.toaster.showError('An unexpected error occurred');
        } finally {
            this.isLoading = false;
        }
    }

    close(result: boolean = false): void {
        if (this.isDialog) {
            this.dialogRef!.close(result);
        } else {
            // Navigate back to the list
            if (this.contextType === AppMasterContextCategory.DEPARTMENT) {
                this.router.navigate(['/organization/departments']);
            } else if (this.contextType === AppMasterContextCategory.DESIGNATION) {
                this.router.navigate(['/organization/designations']);
            } else {
                this.router.navigate(['/organization']);
            }
        }
    }

    cancel(): void {
        this.close();
    }
}
